import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Sigma } from '../components/Icons.jsx';

// Pre-2024 UCAT: VR + DM + QR + AR, each 300-900, total 1200-3600
// 2024 onwards: VR + DM + QR (AR retired), each 300-900, total 900-2700
// Conversion: take 3-section equivalent — drop AR, scale to 2700 max OR
// for "what would my old score have been if AR matched my average": old = (new/3) * 4
// Decile mapping uses ACER-published 2024 stats vs 2023 cohort.

const oldTo2024Estimate = (oldTotal) => Math.round((oldTotal / 4) * 3);
const new2024ToOldEstimate = (newTotal) => Math.round((newTotal / 3) * 4);

const oldDecileBands = [
  { min: 2400, decile: 'Top 10%', note: '~99th percentile equivalent in old 4-section UCAT.' },
  { min: 2200, decile: 'Top 25%', note: 'Comfortably above the cutoff of most threshold schools (old format).' },
  { min: 2000, decile: 'Top 50%', note: 'Around national median (old format).' },
  { min: 0, decile: 'Below 50%', note: 'Below median (old format) — focus shortlist on holistic schools.' }
];
const newDecileBands = [
  { min: 2780, decile: 'Top 10%', note: 'Reaches Oxford / Cambridge ranking-based cutoffs (2024 format).' },
  { min: 2600, decile: 'Top 25%', note: 'Strong — opens UCL, Imperial, KCL, Manchester ranges.' },
  { min: 2400, decile: 'Top 50%', note: 'Around 2024 median — many schools still in range.' },
  { min: 0, decile: 'Below 50%', note: 'Below 2024 median — favour holistic and threshold-style schools.' }
];

export default function UcatConverter() {
  const [mode, setMode] = useState('oldToNew');
  const [score, setScore] = useState(2200);

  const min = mode === 'oldToNew' ? 1200 : 900;
  const max = mode === 'oldToNew' ? 3600 : 2700;

  const converted = useMemo(() => {
    return mode === 'oldToNew' ? oldTo2024Estimate(score) : new2024ToOldEstimate(score);
  }, [mode, score]);

  const decile = useMemo(() => {
    const bands = mode === 'oldToNew' ? oldDecileBands : newDecileBands;
    return bands.find((b) => score >= b.min);
  }, [mode, score]);

  return (
    <>
      <div className="page-head">
        <Link to="/plan" className="back-link"><ArrowLeft width="16" height="16" />Back to Plan</Link>
        <h1 style={{ marginTop: 12 }}>UCAT 2024 ↔ 2025 converter</h1>
        <p className="subtitle">Compare scores between the old 4-section UCAT (VR+DM+QR+AR) and the 2024+ 3-section format. Abstract Reasoning was retired in 2024.</p>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 17 }}>Direction</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
          <Toggle active={mode === 'oldToNew'} onClick={() => { setMode('oldToNew'); setScore(2200); }}>Old → New</Toggle>
          <Toggle active={mode === 'newToOld'} onClick={() => { setMode('newToOld'); setScore(2400); }}>New → Old</Toggle>
        </div>

        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)' }}>
          {mode === 'oldToNew' ? 'Your old-format total (VR + DM + QR + AR · 1200–3600)' : 'Your 2024 total (VR + DM + QR · 900–2700)'}
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 8 }}>
          <input type="range" min={min} max={max} step="10" value={score}
            onChange={(e) => setScore(Number(e.target.value))} style={{ flex: 1, accentColor: 'var(--brand-purple)' }} />
          <input type="number" min={min} max={max} value={score}
            onChange={(e) => setScore(Math.max(min, Math.min(max, Number(e.target.value) || 0)))}
            style={{ width: 100, padding: '8px 10px', fontSize: 18, fontWeight: 700, border: '1px solid var(--border-strong)', borderRadius: 8, textAlign: 'right' }} />
        </div>
      </div>

      <div className="card" style={{ marginBottom: 18, borderLeft: '5px solid var(--brand-purple)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {mode === 'oldToNew' ? 'Estimated 2024 equivalent' : 'Estimated old-format equivalent'}
        </div>
        <h2 style={{ margin: '8px 0 0', fontSize: 'clamp(36px, 6vw, 48px)', display: 'flex', alignItems: 'baseline', gap: 12 }}>
          {converted}
          <span style={{ color: 'var(--text-muted)', fontSize: 18, fontWeight: 500 }}>/ {mode === 'oldToNew' ? '2700' : '3600'}</span>
        </h2>
        {decile && (
          <p style={{ margin: '10px 0 0', color: 'var(--text-2)' }}>
            <strong>{decile.decile}</strong> — {decile.note}
          </p>
        )}
      </div>

      <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
        Heuristic only: AR was its own section, so removing it doesn't translate 1:1. Treat this as a sanity check, not an official rescore. For "where does my score actually land me?", use{' '}
        <Link to="/where-can-i-apply" style={{ color: 'var(--accent)', fontWeight: 600 }}>Where can I apply</Link>.
      </p>
    </>
  );
}

function Toggle({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
      borderRadius: 999, border: `1px solid ${active ? 'var(--brand-purple)' : 'var(--border)'}`,
      background: active ? 'var(--brand-purple)' : '#fff',
      color: active ? '#fff' : 'var(--text-2)'
    }}>{children}</button>
  );
}
